import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { getLevels } from '../../api';
import './Accueil.css';

const GAMES = [
  {
    title: 'Le Miroir des Émotions',
    description: 'Associe les émotions à leurs expressions et apprends à les reconnaître.',
    icon: '🪞',
    to: '/app/jeux/miroir-emotions',
    theme: 'blue',
  },
  {
    title: "L'Aventure de l'Amitié",
    description: 'Aide tes amis en choisissant les meilleures actions empathiques.',
    icon: '🌟',
    to: '/app/jeux/aventure-amitie',
    theme: 'purple',
  },
];

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Bonjour';
  if (h < 18) return 'Bon après-midi';
  return 'Bonsoir';
}

function Accueil() {
  const { user } = useAuth();
  const [levels, setLevels] = useState([]);

  useEffect(() => {
    getLevels()
      .then((r) => setLevels(r.data.sort((a, b) => a.level - b.level)))
      .catch(() => {});
  }, []);

  const calculateProgress = () => {
    if (levels.length === 0) {
      return { percentage: 0, xpInCurrentLevel: 0, xpNeededForNextLevel: null };
    }

    const currentXP = user?.experience_points || 0;
    let currentLevel = levels[0];
    let nextLevel = levels[1] || null;

    for (let i = 0; i < levels.length; i++) {
      if (levels[i].experience_required <= currentXP) {
        currentLevel = levels[i];
        nextLevel = levels[i + 1] || null;
      } else {
        break;
      }
    }

    if (!nextLevel) {
      return { percentage: 100, xpInCurrentLevel: 0, xpNeededForNextLevel: 0, isMaxLevel: true };
    }

    const xpInCurrentLevel = currentXP - currentLevel.experience_required;
    const xpNeededForNextLevel = nextLevel.experience_required - currentLevel.experience_required;
    const percentage = Math.min(100, Math.round((xpInCurrentLevel / xpNeededForNextLevel) * 100));

    return { percentage, xpInCurrentLevel, xpNeededForNextLevel };
  };

  const progress = calculateProgress();
  const levelNumber = user?.current_level?.level ?? '?';
  const totalXP = user?.experience_points || 0;

  return (
    <div className="accueil-page">

      {/* ── Hero ── */}
      <section className="accueil-hero">
        <div className="hero-text">
          <h1>{getGreeting()}, {user?.first_name} !</h1>
          <p>Prêt à jouer et progresser aujourd'hui ?</p>
        </div>

        <div className="hero-xp">
          <div className="xp-level-badge">
            <span className="xp-level-label">Niveau</span>
            <span className="xp-level-number">{levelNumber}</span>
          </div>

          <div className="xp-details">
            <div className="xp-bar-track">
              <div
                className="xp-bar-fill"
                style={{ width: `${progress.percentage}%` }}
              />
            </div>
            <div className="xp-bar-labels">
              {progress.isMaxLevel ? (
                <span className="xp-max">Niveau maximum !</span>
              ) : progress.xpNeededForNextLevel !== null ? (
                <>
                  <span>{progress.xpInCurrentLevel} / {progress.xpNeededForNextLevel} XP</span>
                  <span>{progress.percentage}%</span>
                </>
              ) : (
                <span>{totalXP} XP</span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Jeux ── */}
      <section className="accueil-section">
        <h2 className="accueil-section-title">Jouer maintenant</h2>
        <div className="accueil-games-grid">
          {GAMES.map((game) => (
            <Link
              key={game.to}
              to={game.to}
              className={`accueil-game-card game-${game.theme}`}
            >
              <span className="game-card-icon" aria-hidden="true">{game.icon}</span>
              <div className="game-card-body">
                <h3>{game.title}</h3>
                <p>{game.description}</p>
              </div>
              <span className="game-card-arrow">→</span>
            </Link>
          ))}
        </div>
      </section>

      {/* ── Équipe (conditionnelle) ── */}
      {user?.team_id && (
        <section className="accueil-section">
          <h2 className="accueil-section-title">Mon équipe</h2>
          <Link to="/app/equipes" className="accueil-team-card">
            <span className="team-card-icon" aria-hidden="true">👥</span>
            <div className="team-card-body">
              <p className="team-card-title">Voir mon équipe</p>
              <p className="team-card-sub">Retrouve tes coéquipiers</p>
            </div>
            <span className="team-card-arrow">→</span>
          </Link>
        </section>
      )}

    </div>
  );
}

export default Accueil;
